import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Smartphone, Target } from "lucide-react";
import { useNavigate } from "react-router-dom";
import stylingIllustration from "@/assets/styling-illustration.gif";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";

interface WhyChooseCelloBassProps {
  onGetStartedClick?: () => void;
  queued?: boolean;
  /** When provided, replaces the styling illustration with a photo collage (men's page). */
  images?: string[];
  /** Splash pages: wraps the section in the #features anchor that clears the mobile sticky header. */
  mobileRedesign?: boolean;
}

const WhyChooseCelloBass = ({ onGetStartedClick, queued = false, images, mobileRedesign = false }: WhyChooseCelloBassProps = {}) => {
  const navigate = useNavigate();
  const [imgIndex, setImgIndex] = useState(0);
  const reduceMotion = useReducedMotion();

  // Auto-advance the image carousel (men's page).
  useEffect(() => {
    if (!images || images.length < 2 || reduceMotion) return;
    const id = setInterval(() => setImgIndex((i) => (i + 1) % images.length), 3500);
    return () => clearInterval(id);
  }, [images, reduceMotion]);

  const desktop = (
    <section id={mobileRedesign ? undefined : "features"} className="relative bg-[#FFF7F0] pt-16 pb-20 sm:py-32 px-6 sm:px-8 overflow-hidden">
      <div className="max-w-[1024px] mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row gap-9 sm:gap-12 lg:gap-16 items-stretch sm:items-center">
          
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="flex flex-col gap-7 sm:gap-8 lg:w-1/2">
            <div className="flex flex-col gap-5 sm:gap-4">
              <h2 className="text-[38px] sm:text-5xl lg:text-[52px] font-bold font-playfair leading-[1.05] tracking-normal sm:tracking-tight text-[#0f172a]">
                They're going to notice.{" "}
                <span className="text-[#FF8B11] relative inline-block italic">
                  Let them.
                </span>
              </h2>
              <p className="text-[#334155] text-[15px] sm:text-[14px] md:text-[15px] leading-[1.6] sm:leading-relaxed max-w-none sm:max-w-[360px] font-normal sm:font-medium mt-0 sm:mt-2 mb-0 sm:mb-2 lg:mb-0 border-l-[3px] border-[#FF8B11] pl-5">
                Cello digitizes your closet in under 30 minutes, then our expert stylists create styles tailored just to you. Your job? Look and feel good.
              </p>
            </div>
            
            <motion.div className="w-fit mt-0 sm:mt-2">
              <Button
                onClick={() => onGetStartedClick ? onGetStartedClick() : navigate('/auth', { state: { tab: 'signup' } })}
                className={`text-white rounded-md h-12 px-6 py-0 sm:h-auto sm:px-8 sm:py-5 lg:py-6 text-[11px] font-bold uppercase tracking-[0.15em] sm:tracking-wider shadow-none sm:shadow-lg transition-all border-b-2 hover:translate-y-[-1px] active:translate-y-[1px] active:border-b-0 w-auto ${queued ? "bg-[#16A34A] hover:bg-[#15803D] border-[#0f7a37]" : "bg-[#0f172a] hover:bg-[#1e293b] border-[#020617]"}`}
              >
                {queued ? "You're on the list" : "Join the waitlist"}
              </Button>
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }} className="flex flex-wrap justify-center lg:justify-end relative lg:w-1/2 w-full">
            <div className="absolute inset-0 bg-[#FF8B11]/5 rounded-full blur-3xl transform scale-90" />
            {images && images.length > 0 ? (
              // Auto-advancing single-image carousel — native 3:2, nothing cropped.
              <>
              <div className="relative z-10 w-full max-w-[520px] aspect-[3/2] overflow-hidden rounded-2xl shadow-[0_20px_40px_-20px_rgba(15,23,42,0.35)] sm:shadow-xl">
                <AnimatePresence>
                  <motion.img
                    key={imgIndex}
                    src={images[imgIndex]}
                    alt=""
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8, ease: "easeInOut" }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </AnimatePresence>
                {images.length > 1 && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 hidden sm:flex gap-1.5">
                    {images.map((_, i) => (
                      <span
                        key={i}
                        className={`h-1.5 rounded-full transition-all duration-300 ${i === imgIndex ? "w-5 bg-white" : "w-1.5 bg-white/60"}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div className="sm:hidden basis-full mt-4 flex justify-center gap-1.5" aria-hidden="true">
                  {images.map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 rounded-full transition-all duration-300 ${i === imgIndex ? "w-5 bg-[#FF8B11]" : "w-1.5 bg-[#d6d3d1]"}`}
                    />
                  ))}
                </div>
              )}
              </>
            ) : (
              <motion.img
                whileHover={{ rotate: [0, -1, 1, 0], scale: 1.02 }}
                transition={{ duration: 0.5 }}
                src={stylingIllustration}
                alt="Professional styling illustration"
                className="w-full max-w-[500px] lg:max-w-[550px] h-auto object-contain relative z-10 drop-shadow-2xl"
              />
            )}
          </motion.div>
          
        </div>
      </div>
    </section>
  );

  if (!mobileRedesign) return desktop;

  // One responsive tree; the anchor moves to this wrapper so the header's
  // "Features" link lands below the mobile sticky group.
  return (
    <div id="features" className="scroll-mt-[var(--sticky-h,0px)] md:scroll-mt-0">
      {desktop}
    </div>
  );
};

export default WhyChooseCelloBass;